// src/modules/operaciones/inventarios/hooks/usePrecios.js
//
// Cálculo de precios contra el backend (lotes, progreso y cancelación).
import { useState, useEffect, useCallback, useRef } from "react"; // Hooks usados
import { fmt } from "../../../../utils/format"; // Formato de números compartido
import { API, LOTE_PRECIOS, DURACION_CHECK_MS, txt, lineasDe } from "../utils/constantes"; // Constantes y helpers
import { postJson } from "../utils/api"; // POST JSON con timeout
import { necesitaPrecio, juntarRespuestas, resumenPrecios } from "../utils/reglasPrecio"; // Reglas de precio

// Estado inicial del hook usePrecios. NO mutar.
const SIN_CALCULO = { activo: false, ajustandoId: null, progreso: null };

// Cálculo de precios contra el backend (todos los ajustes o uno solo) con progreso y cancelación.
export default function usePrecios({ detalle, detallePorAjuste, setMensaje }) {
  const [precios, setPrecios] = useState({}); // Mapa arti_cod → precio calculado
  const [calculo, setCalculo] = useState(SIN_CALCULO); // Estado del cálculo en curso
  const [checks, setChecks] = useState({}); // Ids de ajustes que muestran "✓ Listo"
  const ctrl = useRef(null); // Controlador de cancelación del cálculo actual
  const timeoutsRef = useRef(new Set()); // Timeouts pendientes para limpiar al desmontar
  const activoRef = useRef(false); // guard síncrono: el estado `calculo` no se actualiza hasta el siguiente render

  useEffect(
    () => () => { // Limpieza al desmontar el componente
      ctrl.current?.abort(); // Cancela cualquier petición en curso
      timeoutsRef.current.forEach(clearTimeout); // Cancela los timeouts pendientes
      timeoutsRef.current.clear(); // Vacía el registro de timeouts
    },
    []
  );

  // Cancela todo y borra los precios (se usa al recargar datos)
  const reiniciar = useCallback(() => {
    ctrl.current?.abort(); // Cancela la petición en curso
    activoRef.current = false; // Libera el guard
    setCalculo(SIN_CALCULO); // Restablece el estado de cálculo
    setPrecios({}); // Borra los precios calculados
  }, []);

  const setProgreso = (progreso) => setCalculo((c) => ({ ...c, progreso })); // Actualiza solo el progreso

  // Envoltorio común: controla el guard, la cancelación y el manejo de errores
  const conCalculo = async (ajustandoId, reintentar, tarea) => {
    if (activoRef.current) return; // ya hay un cálculo en curso: se ignora el segundo disparo (p. ej. doble click)
    activoRef.current = true; // Activa el guard
    const controller = new AbortController(); // Nuevo controlador de cancelación
    ctrl.current = controller; // Lo guarda para poder cancelar
    setCalculo((c) => ({ ...c, activo: true, ajustandoId })); // Marca el cálculo como activo
    try {
      await tarea(controller.signal); // Ejecuta la tarea con la signal
    } catch (err) {
      if (err.name === "AbortError") return; // Cancelación voluntaria: no muestra error
      setMensaje({ type: "error", reintentar, text: `Error calculando precios: ${err.message}.` }); // Muestra el error con opción de reintentar
    } finally {
      if (ctrl.current === controller) { // Solo limpia si sigue siendo el cálculo vigente
        activoRef.current = false; // Libera el guard
        setCalculo(SIN_CALCULO); // Restablece el estado
      }
    }
  };

  // Muestra el mensaje final del cálculo según haya fallado Sysplus o no
  const avisar = (titulo, { mapa, sysplusOk, errores, aviso }) => {
    const resumen = `${titulo}: ${resumenPrecios(mapa)}.`; // Texto resumen del resultado
    setMensaje(
      sysplusOk
        ? { type: "success", text: `${resumen} (Modo temporal)${aviso ? ` ${aviso}` : ""}` } // Éxito completo
        : { type: "warning", text: `${resumen} ${errores} artículo(s) sin consultar. ${aviso || "No se pudo consultar Sysplus."}` } // Éxito parcial con advertencia
    );
  };

  // Sin argumento recalcula todo el `detalle`; con una lista de ajustes, solo las líneas de esos ajustes.
  const recalcular = (ajustesObjetivo) => {
    const parcial = Array.isArray(ajustesObjetivo); // true si se pasó una lista de ajustes
    const lineasObjetivo = parcial ? ajustesObjetivo.flatMap((a) => lineasDe(detallePorAjuste, a)) : detalle; // Líneas a procesar
    if (lineasObjetivo.length === 0) { // Sin líneas
      return setMensaje({ type: "warning", text: "No hay líneas de detalle para calcular precios." }); // Avisa y termina
    }

    const unicos = new Map(); // Artículos únicos que necesitan precio
    lineasObjetivo.forEach((d) => { // Recorre las líneas objetivo
      const cod = txt(d.ARTI_COD); // Código del artículo
      if (necesitaPrecio(d) && !unicos.has(cod)) unicos.set(cod, { arti_cod: cod, descripcion: txt(d.AJDE_DESC), bode_cod: txt(d.BODE_COD) }); // Guarda el primero de cada código
    });
    if (unicos.size === 0) { // Ninguna línea necesita precio
      return setMensaje({ type: "warning", text: "No hay líneas que necesiten precio (todas tienen costo o su cantidad es 0)." }); // Avisa y termina
    }

    return conCalculo(null, () => recalcular(ajustesObjetivo), async (signal) => { // Ejecuta el cálculo con guard y reintento
      const lista = [...unicos.values()]; // Artículos a consultar como array
      const totalLotes = Math.ceil(lista.length / LOTE_PRECIOS); // Cantidad de lotes necesarios
      setProgreso({ actual: 0, total: totalLotes, texto: `Procesando ${fmt(lista.length, 3)} artículo(s) en ${totalLotes} lote(s)…` }); // Progreso inicial
      const respuestas = []; // Respuestas de cada lote
      for (let i = 0, lote = 1; i < lista.length; i += LOTE_PRECIOS, lote++) { // Itera por lotes
        respuestas.push(await postJson(`${API}/precios/calcular`, { articulos: lista.slice(i, i + LOTE_PRECIOS) }, signal)); // Envía el lote al backend
        setProgreso({ actual: lote, total: totalLotes, texto: `Lote ${lote} de ${totalLotes} completado…` }); // Actualiza el progreso
      }
      const r = juntarRespuestas(respuestas); // Consolida las respuestas
      setPrecios((prev) => (parcial ? { ...prev, ...r.mapa } : r.mapa)); // parcial: conserva lo ya calculado fuera del filtro
      avisar(`Precios calculados para ${fmt(Object.keys(r.mapa).length, 3)} artículo(s)`, r); // Muestra el resultado
    });
  };

  // Calcula los precios de un solo ajuste
  const ajustar = (ajuste) => {
    const id = ajuste.AJUS_ID; // Id del ajuste
    const lineas = lineasDe(detallePorAjuste, ajuste); // Líneas del ajuste
    if (lineas.length === 0) return setMensaje({ type: "warning", text: `El ajuste #${id} no tiene líneas de detalle.` }); // Sin líneas: avisa
    if (!lineas.some(necesitaPrecio)) { // Ninguna línea necesita precio
      return setMensaje({
        type: "warning", // Tipo advertencia
        text: `El ajuste #${id} no tiene líneas que necesiten precio (todas tienen costo o su cantidad es 0).`, // Texto del aviso
      });
    }

    return conCalculo(id, () => ajustar(ajuste), async (signal) => { // Ejecuta el cálculo con guard y reintento
      setProgreso({ actual: 0, total: 1, texto: `Consultando costo promedio del ajuste #${id}…` }); // Progreso inicial
      const data = await postJson(`${API}/precios/calcular-ajuste`, { ajuste_id: id }, signal); // Pide al backend calcular ese ajuste
      setProgreso({ actual: 1, total: 1, texto: `Ajuste #${id} completado.` }); // Progreso completo
      const r = juntarRespuestas([data]); // Consolida la respuesta
      setPrecios((prev) => ({ ...prev, ...r.mapa })); // Agrega los precios al mapa existente
      setChecks((prev) => ({ ...prev, [id]: true })); // Muestra "✓ Listo" en la fila
      const t = setTimeout(() => { // Programa ocultar el check
        timeoutsRef.current.delete(t); // Se quita del registro al ejecutarse
        setChecks((prev) => ({ ...prev, [id]: false })); // Oculta el check
      }, DURACION_CHECK_MS); // Tras 2 segundos
      timeoutsRef.current.add(t); // Registra el timeout para limpiarlo si se desmonta
      avisar(`Ajuste #${id}: precios calculados para ${fmt(Object.keys(r.mapa).length, 3)} artículo(s)`, r); // Muestra el resultado
    });
  };

  // Valores y funciones que expone el hook
  return {
    precios, checks, recalcular, ajustar, reiniciar,
    calculando: calculo.activo, ajustandoId: calculo.ajustandoId, progreso: calculo.progreso, // Estado derivado del cálculo
  };
}